import { Order, OrbOrderStatus } from '@/types/orderTypes';

export const getOrderStatusClass = (
  status: OrbOrderStatus,
): 'ready' | 'preparing' => {
  return status === OrbOrderStatus.READY ? 'ready' : 'preparing';
};

/**
 * Extracts the display order number from various order number formats
 *
 * @param orderNumber - Full order number (e.g., "SP-1-115-20250808", "123456789")
 * @returns Last 3 digits for display (e.g., "115", "789")
 */
export const getOrderNumber = (orderNumber?: string): string => {
  if (!orderNumber) return '';

  const parts = orderNumber.split('-');
  return parts[2] ? parts[2] : orderNumber.slice(-3);
};

/**
 * Formats customer name for display
 * - Capitalizes the first letter of the first word
 * - Converts remaining words to initials (no trailing dot), e.g., "Tony Stark" -> "Tony S"
 */
const formatCustomerName = (name: string): string => {
  if (!name) return '';

  const words = name.trim().split(/\s+/);
  if (words.length === 0) return '';

  // Capitalize first word
  const firstName =
    words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();

  if (words.length === 1) {
    return firstName;
  }

  // Convert remaining words to initials
  const initials = words
    .slice(1)
    .map((word, index, array) => {
      const initial = word.charAt(0).toUpperCase();
      // Add dot after each initial except the last one
      return index === array.length - 1 ? initial : initial + '.';
    })
    .join('');

  return `${firstName} ${initials}`;
};

export const getOrderDisplayIdentifier = (order: Order): string => {
  if (order.isOnline) {
    return formatCustomerName(order.customerName ?? '');
  }
  const orderNumber = getOrderNumber(order.orderNumber);
  if (order.tableName) return order.tableName;
  if (order.customerName) return formatCustomerName(order.customerName);
  if (order.tokenNumber) return String(order.tokenNumber);
  return orderNumber;
};

export const getOrderDisplaySubIdentifier = (order: Order): string => {
  if (order.isOnline) {
    return `${order.salesChannelName}#${
      order.tableName ? order.tableName : order.orderNumber
    }`;
  }
  return '';
};
